import {
  Color,
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Points,
  AdditiveBlending,
  Vector3,
  MathUtils
} from 'three';
import vertex from '../../../shaders/outflow/vertexWind.glsl';
import fragment from '../../../shaders/outflow/fragmentWind.glsl';
import { animateParts } from '../utils/many-bodies.js';
import { sceneManager } from '../script.js';
import { randomNormal } from '../helpers.js';

export class PulsarNebula {
  constructor(numPoints, centerElement, geometryConfig = {},attrNeb ) {
    this.numPoints = numPoints;
    this.centerElement = centerElement;
    this.maxSpeed = 3e-3;
    this.minSpeed = 1e-4;
    this.attrNeb = attrNeb
    this.format = attrNeb.format;
    this.normal = new Vector3(0, 1, 0);

    const attrs = this._generateToroidalAttrs(numPoints);
    this._initGeometry(attrs, geometryConfig);
  }

  _generateToroidalAttrs(numPoints) {
    const { r, R, thetaSet, phiSet } = this.format;
    const positions = [];
    const aColors = [];
    const sizes = [];
    const randomValues = [];
    const angles = [];
    this.speeds = [];

    for (let i = 0; i < numPoints; i++) {
      const tubeRadius = Math.abs(randomNormal(10, r / 0.12));
      const theta = randomNormal(0, Math.PI * thetaSet);
      const phi = randomNormal(Math.PI, Math.PI * phiSet);

      const x = (R + tubeRadius * Math.cos(theta)) * Math.cos(phi);
      const z = (R + tubeRadius * Math.cos(theta)) * Math.sin(phi);
      const y = tubeRadius * Math.sin(theta);

      positions.push(x, y, z);

      const { color, size } = colorSizeAleat(this.attrNeb);
      aColors.push(color.r, color.g, color.b);
      sizes.push(size);

      randomValues.push(Math.random() );
      angles.push(Math.random() * Math.PI * 2);

      this.speeds.push(Math.random() * (this.maxSpeed - this.minSpeed) + this.minSpeed);
    }

    return { positions, aColors, sizes, randomValues, angles};
  }

  _initGeometry(attrs, geomConfig = {}) {
    const {maxRadius } = geomConfig;
    this.geometry = new BufferGeometry();

    this.geometry.setAttribute('position',    new BufferAttribute(new Float32Array(attrs.positions),    3));
    this.geometry.setAttribute('aColor',       new BufferAttribute(new Float32Array(attrs.aColors),       3));
    this.geometry.setAttribute('sizes',       new BufferAttribute(new Float32Array(attrs.sizes),       1));
    this.geometry.setAttribute('randomValue', new BufferAttribute(new Float32Array(attrs.randomValues), 1));
    this.geometry.setAttribute('angle',       new BufferAttribute(new Float32Array(attrs.angles),       1));

    const defaultRadius = this.format.R + this.format.r;
    this.uniforms = {
      u_time: { value: 0 },
      u_r:    { value: maxRadius || defaultRadius },
      opacity:{ value: 1.0 },
      u_center:{value:this.centerElement.mesh.position},
      u_camPos:{value:sceneManager.camera.position},
      u_exponent:{value:1},
      u_probability:{value:0.8}

    };

    this.material = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending
    });

    this.points = new Points(this.geometry, this.material);
    sceneManager.scene.add(this.points);
  }
  animate(delta) {
    if (!this.points) return;
    this.points.position.copy(this.centerElement.mesh.position);
    animateParts(this.points, delta, this.speeds);
    this.material.uniforms.u_time.value += delta *1e-7;
    this.material.uniforms.u_center.value = this.centerElement.mesh.position;
    this.material.uniforms.u_camPos.value = sceneManager.camera.position;

     this.material.uniforms.opacity.value = this.calculateOpacity(
      sceneManager.camera,
      this.centerElement.mesh.position,
      this.normal,
      0.08,
      0.04
    );
    this.material.visible = sceneManager.commands.controls.spectrum
  }
    calculateOpacity(camera, center, normal, a, b) {
      const camDir = new Vector3().subVectors(camera.position, center).normalize();
      const dot = Math.abs(camDir.dot(normal));
      return MathUtils.clamp(a * (1 - (1-dot)) + b, 0, 1);
    }
}

export function colorSizeAleat(attr) {
  const total = attr.percentage.reduce((a, b) => a + b, 0);
  const rnd = Math.random() * total;
  let sum = 0, idx = 0;
  for (let i = 0; i < attr.percentage.length; i++) {
    sum += attr.percentage[i];
    if (rnd <= sum) { idx = i; break; }
  }
  const col = attr.color[idx];
  return {
    color: new Color(((col >> 16) & 255) / 255,
                     ((col >> 8) & 255) / 255,
                      (col & 255) / 255),
    size: attr.size[idx]
  };
}